import { useIntl } from 'react-intl';
import { Box, Chip, Stack, useTheme } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';

/** Small coloured line used in the legend to illustrate edge styles. */
const LegendLine = ({ color, dashed }: { color: string; dashed?: boolean }) => (
  <Box
    sx={{
      width: 24,
      height: 0,
      borderTop: `2px ${dashed ? 'dashed' : 'solid'} ${color}`,
      flexShrink: 0,
    }}
  />
);

/** Small coloured rectangle illustrating a node border style. */
const LegendNodeSample = ({
  color,
  dashed,
}: {
  color: string;
  dashed?: boolean;
}) => (
  <Box
    sx={{
      width: 16,
      height: 12,
      border: `1.5px ${dashed ? 'dashed' : 'solid'} ${color}`,
      borderRadius: borderRadius('s'),
      flexShrink: 0,
    }}
  />
);

export const ConfigMapLegend = ({
  showRules,
  onToggleRules,
  hasRules = true,
  showHooks,
  onToggleHooks,
  hasHooks = true,
}: {
  showRules: boolean;
  onToggleRules: () => void;
  hasRules?: boolean;
  showHooks: boolean;
  onToggleHooks: () => void;
  hasHooks?: boolean;
}) => {
  const theme = useTheme();
  const intl = useIntl();

  const plainChipSx = {
    backgroundColor: 'transparent',
    border: 'none',
  };

  const toggleChipSx = (active: boolean) => ({
    opacity: active ? 1 : 0.35,
    backgroundColor: active ? 'action.selected' : 'transparent',
    border: 'none',
    px: 1,
  });

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Chip
        size="small"
        icon={<LegendLine color={theme.palette.primary.main} />}
        label={intl.formatMessage({
          id: 'containers.settings.queues.queue.overview.legend.dependency',
        })}
        sx={plainChipSx}
      />
      <Chip
        size="small"
        icon={<LegendLine color={theme.palette.error.main} />}
        label={intl.formatMessage({
          id: 'containers.settings.queues.queue.overview.legend.missingDep',
        })}
        sx={plainChipSx}
      />
      <Chip
        size="small"
        icon={<LegendLine color={theme.palette.error.main} dashed />}
        label={intl.formatMessage({
          id: 'containers.settings.queues.queue.overview.legend.cycle',
        })}
        sx={plainChipSx}
      />
      <Chip
        size="small"
        icon={<LegendLine color={theme.palette.text.disabled} dashed />}
        label={intl.formatMessage({
          id: 'containers.settings.queues.queue.overview.legend.runAfter',
        })}
        sx={plainChipSx}
      />
      <Chip
        size="small"
        icon={<LegendNodeSample color={theme.palette.primary.main} />}
        label={intl.formatMessage({
          id: 'containers.settings.queues.queue.overview.legend.field',
        })}
        sx={plainChipSx}
      />
      {hasHooks && (
        <Chip
          size="small"
          icon={<LegendNodeSample color={theme.palette.warning.main} />}
          label={intl.formatMessage({
            id: 'containers.settings.queues.queue.overview.legend.hook',
          })}
          onClick={onToggleHooks}
          sx={toggleChipSx(showHooks)}
        />
      )}
      {hasRules && (
        <Chip
          size="small"
          icon={<LegendNodeSample color={theme.palette.secondary.main} />}
          label={intl.formatMessage({
            id: 'containers.settings.queues.queue.overview.legend.rule',
          })}
          onClick={onToggleRules}
          sx={toggleChipSx(showRules)}
        />
      )}
      <Chip
        size="small"
        icon={<LegendNodeSample color={theme.palette.divider} dashed />}
        label={intl.formatMessage({
          id: 'containers.settings.queues.queue.overview.legend.inactiveExternal',
        })}
        sx={plainChipSx}
      />
    </Stack>
  );
};
